import { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../utils/api';

export function useEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/events');
      setEvents(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Filter events by type
  const getEventsByType = (type) => {
    if (Array.isArray(type)) {
      return events.filter((e) => type.includes(e.type));
    }
    return events.filter((e) => e.type === type);
  };

  // Get tasks (type='task')
  const getTasks = () => getEventsByType('task');

  // Get calendar events (milestone, decision, meeting)
  const getCalendarEvents = () => getEventsByType(['milestone', 'decision', 'meeting']);

  // Get overdue items (past due date, not completed)
  const getOverdue = () => {
    const today = new Date().toISOString().split('T')[0];
    return events.filter(
      (e) => e.date < today && e.status !== 'completed' && e.status !== 'cancelled'
    );
  };

  // Get upcoming items (next 30 days)
  const getUpcoming = () => {
    const today = new Date();
    const in30Days = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
    const todayStr = today.toISOString().split('T')[0];
    const endDateStr = in30Days.toISOString().split('T')[0];

    return events.filter(
      (e) =>
        e.date >= todayStr &&
        e.date <= endDateStr &&
        e.status !== 'completed' &&
        e.status !== 'cancelled'
    );
  };

  // Create event
  const createEvent = async (eventData) => {
    try {
      const response = await apiCall('/api/events', {
        method: 'POST',
        body: JSON.stringify(eventData),
      });
      setEvents([...events, response]);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Update event
  const updateEvent = async (eventId, updates) => {
    try {
      await apiCall(`/api/events/${eventId}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      setEvents(
        events.map((e) =>
          e.id === eventId ? { ...e, ...updates, updatedAt: new Date().toISOString() } : e
        )
      );
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Delete event
  const deleteEvent = async (eventId) => {
    try {
      await apiCall(`/api/events/${eventId}`, { method: 'DELETE' });
      setEvents(events.filter((e) => e.id !== eventId));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  return {
    events,
    loading,
    error,
    loadEvents,
    getTasks,
    getCalendarEvents,
    getOverdue,
    getUpcoming,
    createEvent,
    updateEvent,
    deleteEvent,
  };
}
